// Склейка RUM поверх Grafana Faro Web SDK (SAEX-4495, решение - SAEX-4493).
//
// Faro сам собирает web-vitals, необработанные ошибки, Navigation и Resource Timing. Здесь
// только то, чего в нём нет:
//   - ошибки загрузки картинок, скриптов и CSS: событие error не всплывает, его видно только
//     в фазе capture на window;
//   - счётчики удачных и неудачных загрузок, иначе долю ошибок не посчитать;
//   - сетевые отказы и таймауты fetch и XHR: у такого запроса нет кода ответа, и Resource
//     Timing его не показывает;
//   - адреса без id и query: в RUM не уходят персональные данные и номера кабинетов.
//
// Копия этого файла лежит в front, miniapp и help-center. Канон - front,
// src/shared/lib/rum/glue.ts: правка идёт во все три репозитория.

export type HostGroup = "self" | "sa" | "s3" | "wbbasket" | "ozone" | "other";

export type PushEvent = (name: string, attributes: Record<string, string>) => void;

// Путь приёмника на своём домене. Маршрут - ops edge/k8s/base/ingressroutes.yaml.
export const COLLECT_PATH = "/client-health/collect";

const ID_SEGMENT =
  /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|[A-Za-z0-9_-]{24,})$/i;
const MAX_SEGMENTS = 6;

// Сегмент пути с id, токеном, email или длинным числом заменяется на :id.
export function routeOf(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean).slice(0, MAX_SEGMENTS);
  const route = parts
    .map((seg) => {
      let s = seg;
      try {
        s = decodeURIComponent(seg);
      } catch {
        // битый escape - сегмент как есть
      }
      return ID_SEGMENT.test(s) || /\d{3,}/.test(s) || s.includes("@") ? ":id" : s;
    })
    .join("/");
  return `/${route}`.slice(0, 160);
}

export function hostGroup(host: string, selfHost: string): HostGroup {
  const h = host.toLowerCase();
  if (!h || h === selfHost) return "self";
  if (h === "s3.seller-alert.com" || h === "s3.timeweb.com" || h.endsWith("twcstorage.ru")) return "s3";
  if (h === "seller-alert.com" || h.endsWith(".seller-alert.com")) return "sa";
  if (h.endsWith("wbbasket.ru") || h.endsWith("wbstatic.net") || h.endsWith("wb.ru")) return "wbbasket";
  if (h.endsWith("ozone.ru") || h.endsWith("ozon.ru")) return "ozone";
  return "other";
}

// Адрес без query, hash и id. Чужой не-http адрес (data:, blob:) - пустая строка.
export function sanitizeUrl(raw: string, base: string): string {
  try {
    const u = new URL(raw, base);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    return `${u.origin}${routeOf(u.pathname)}`;
  } catch {
    return "";
  }
}

// fetch бывает свойством только для чтения: так его ставят MSW и часть расширений браузера.
// Присваивание в строгом режиме тогда бросает, и initRum ронял бы старт приложения.
function setFetch(win: Window & typeof globalThis, f: typeof fetch): void {
  try {
    Object.defineProperty(win, "fetch", { value: f, writable: true, configurable: true, enumerable: true });
  } catch {
    // неконфигурируемое свойство - fetch остаётся без склейки
  }
}

type Outcome = "ok" | "error" | "network" | "timeout" | "http_5xx";

interface GlueOptions {
  pushEvent: PushEvent;
  // Окно, к которому цепляемся; в тестах - окно happy-dom.
  win?: Window & typeof globalThis;
  flushIntervalMs?: number;
}

export interface Glue {
  flush: () => void;
  uninstall: () => void;
}

export function installGlue({ pushEvent, win = window, flushIntervalMs = 30_000 }: GlueOptions): Glue {
  const selfHost = win.location.host.toLowerCase();
  const base = win.location.href;
  // kind|host_group|outcome -> число
  const resourceCounts = new Map<string, number>();
  const apiCounts = new Map<string, number>();
  let fatalReported = false;

  const bump = (m: Map<string, number>, key: string) => m.set(key, (m.get(key) ?? 0) + 1);

  const describe = (target: EventTarget | null): { kind: string; url: string } | null => {
    if (!target || target === win || !(target instanceof win.Element)) return null;
    if (target instanceof win.HTMLImageElement) return { kind: "img", url: target.currentSrc || target.src };
    if (target instanceof win.HTMLScriptElement) return { kind: "script", url: target.src };
    if (target instanceof win.HTMLLinkElement) {
      const rel = target.rel.toLowerCase();
      if (rel.includes("stylesheet")) return { kind: "css", url: target.href };
      if (rel.includes("modulepreload") || rel.includes("preload")) return { kind: "script", url: target.href };
      return null;
    }
    if (target instanceof win.HTMLMediaElement) return { kind: "media", url: target.currentSrc || target.src };
    return null;
  };

  const onResourceError = (e: Event) => {
    const d = describe(e.target);
    if (!d || !d.url) return;
    const clean = sanitizeUrl(d.url, base);
    if (!clean) return;
    const host = new URL(clean).host;
    const group = hostGroup(host, selfHost);
    bump(resourceCounts, `${d.kind}|${group}|error`);
    // Свой скрипт или CSS не загрузился - страница, скорее всего, не работает.
    const fatal = group === "self" && (d.kind === "script" || d.kind === "css");
    pushEvent("resource_error", {
      kind: d.kind,
      host,
      host_group: group,
      url: clean,
      fatal: fatal ? "1" : "0",
    });
    if (fatal && !fatalReported) {
      fatalReported = true;
      pushEvent("page_failed", { kind: d.kind, host_group: group });
    }
  };

  const onResourceLoad = (e: Event) => {
    const d = describe(e.target);
    if (!d || !d.url) return;
    const clean = sanitizeUrl(d.url, base);
    if (!clean) return;
    bump(resourceCounts, `${d.kind}|${hostGroup(new URL(clean).host, selfHost)}|ok`);
  };

  // Vite не смог загрузить чанк динамического import(): элемента нет, событие своё.
  const onPreloadError = (e: Event) => {
    const payload = (e as Event & { payload?: { message?: string } }).payload;
    const m = /https?:\/\/\S+?\.(js|css)/.exec(payload?.message ?? "");
    const clean = m ? sanitizeUrl(m[0], base) : "";
    const host = clean ? new URL(clean).host : selfHost;
    bump(resourceCounts, `chunk|${hostGroup(host, selfHost)}|error`);
    pushEvent("resource_error", {
      kind: "chunk",
      host,
      host_group: hostGroup(host, selfHost),
      url: clean,
      fatal: "1",
    });
  };

  const tracked = (url: URL) => {
    if (url.pathname.startsWith("/client-health")) return false;
    const group = hostGroup(url.host, selfHost);
    return group === "self" || group === "sa";
  };

  const reportApi = (url: URL, method: string, outcome: Outcome, status: number, startedAt: number) => {
    const group = hostGroup(url.host, selfHost);
    bump(apiCounts, `${group}|${outcome}`);
    if (outcome === "ok") return;
    pushEvent("api_error", {
      host: url.host,
      host_group: group,
      route: routeOf(url.pathname),
      method: method.toUpperCase(),
      outcome,
      status: String(status),
      duration_ms: String(Math.round(win.performance.now() - startedAt)),
    });
  };

  const classifyError = (err: unknown): Outcome | null => {
    const name = (err as { name?: string } | null)?.name;
    if (name === "TimeoutError") return "timeout";
    if (name === "AbortError") return null; // отменил сам код или пользователь ушёл со страницы
    return "network";
  };

  const origFetch = win.fetch;
  const wrappedFetch: typeof fetch = async (input, init) => {
    let url: URL;
    try {
      url = new URL(input instanceof Request ? input.url : String(input), base);
    } catch {
      return origFetch(input, init);
    }
    if (!tracked(url)) return origFetch(input, init);
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const startedAt = win.performance.now();
    try {
      const res = await origFetch(input, init);
      reportApi(url, method, res.status >= 500 ? "http_5xx" : "ok", res.status, startedAt);
      return res;
    } catch (err) {
      const outcome = classifyError(err);
      if (outcome) reportApi(url, method, outcome, 0, startedAt);
      throw err;
    }
  };
  setFetch(win, wrappedFetch);

  const XHR = win.XMLHttpRequest.prototype;
  const origOpen = XHR.open;
  const origSend = XHR.send;
  type Meta = { method: string; url: URL | null };
  const metas = new WeakMap<XMLHttpRequest, Meta>();
  XHR.open = function open(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    let parsed: URL | null = null;
    try {
      parsed = new URL(String(url), base);
    } catch {
      parsed = null;
    }
    metas.set(this, { method, url: parsed });
    return (origOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof XHR.open;
  XHR.send = function send(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const meta = metas.get(this);
    if (meta?.url && tracked(meta.url)) {
      const url = meta.url;
      const startedAt = win.performance.now();
      let outcome: Outcome | null = null;
      this.addEventListener("error", () => (outcome = "network"));
      this.addEventListener("timeout", () => (outcome = "timeout"));
      this.addEventListener("abort", () => (outcome = null));
      this.addEventListener("load", () => (outcome = this.status >= 500 ? "http_5xx" : "ok"));
      this.addEventListener("loadend", () => {
        if (outcome) reportApi(url, meta.method, outcome, this.status, startedAt);
      });
    }
    return origSend.call(this, body);
  };

  const flush = () => {
    for (const [key, count] of resourceCounts) {
      const [kind, group, outcome] = key.split("|");
      pushEvent("resource_stats", { kind, host_group: group, outcome, count: String(count) });
    }
    for (const [key, count] of apiCounts) {
      const [group, outcome] = key.split("|");
      pushEvent("api_stats", { host_group: group, outcome, count: String(count) });
    }
    resourceCounts.clear();
    apiCounts.clear();
  };
  const onHidden = () => {
    if (win.document.visibilityState === "hidden") flush();
  };

  win.addEventListener("error", onResourceError, true);
  // load до window не доходит: по спецификации родитель документа для события load - null.
  win.document.addEventListener("load", onResourceLoad, true);
  win.addEventListener("vite:preloadError", onPreloadError);
  // Раньше Faro: его пачка уходит по тем же событиям, и счётчики должны в неё попасть.
  win.document.addEventListener("visibilitychange", onHidden);
  win.addEventListener("pagehide", flush);
  const timer = win.setInterval(flush, flushIntervalMs);

  return {
    flush,
    uninstall: () => {
      win.removeEventListener("error", onResourceError, true);
      win.document.removeEventListener("load", onResourceLoad, true);
      win.removeEventListener("vite:preloadError", onPreloadError);
      win.document.removeEventListener("visibilitychange", onHidden);
      win.removeEventListener("pagehide", flush);
      win.clearInterval(timer);
      setFetch(win, origFetch);
      XHR.open = origOpen;
      XHR.send = origSend;
    },
  };
}
